"use client";

/**
 * Страница темы поездки /themes/[slug]. Вёрстка — как у страницы машины
 * (CarDetailClient): двухколоночная, слева контент темы, справа sticky-поиск.
 * После поиска результат показывается ПРЯМО в правом блоке под формой — карточки
 * по 2 в ряд (как блок «Свободные автомобили» на странице машины), по клику —
 * инлайн-форма брони (телефон + согласия + «Продолжить»). Страницу машины не трогаем.
 */

import Img from '@/components/Img';
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "@/lib/locale-context";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RichContent from "@/components/RichContent";
import { VerticalSearchForm } from "@/components/VerticalSearchForm";
import BookingPanel from "@/components/BookingPanel";
import CityOfficeBadge from "@/components/CityOfficeBadge";
import type { ApiCar } from "@/lib/api";

const filialOf = (s?: string) => {
  const v = (s || "").trim();
  return v && v.toLowerCase() !== "основной офис" ? v : undefined;
};

const PLACEHOLDER = "/images/catalog-car.png";

export type ThemeDetailCity = { slug: string; station: string; name: string; currency: string; badgeCar?: import("@/lib/badge-types").BadgeCfg };

export type ThemeDetailData = {
  title: string;
  subtitle: string;
  image?: string;
  content: string;
  priceFrom?: string;
};

type Results = { cars: ApiCar[]; days: number; from: string; to: string; st: string; et: string } | null;

const toRub = (k?: number) => Math.round((k ?? 0) / 100);
const priceOf = (c: ApiCar) => {
  const p = Math.max(0, toRub(c.rentPrice));
  const pd = Math.max(0, toRub(c.priceDiscount));
  return pd > 0 ? pd : p; // эффективная суточная (Cost/Period)
};

export default function ThemeDetailClient({ theme, city }: { theme: ThemeDetailData; city: ThemeDetailCity | null }) {
  const router = useRouter();
  const { t, locale, href } = useLocale();
  const [results, setResults] = useState<Results>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const cur = city?.currency || "₽";
  const numLocale = locale === "en" ? "en-US" : "ru-RU";

  function handleSearch(cars: ApiCar[], days: number, from: string, to: string, st: string, et: string) {
    // одна карточка на модель (как park-режим), дешевле → дороже
    const seen = new Set<string>();
    const uniq = cars
      .filter((c) => c.guid)
      .sort((a, b) => priceOf(a) - priceOf(b))
      .filter((c) => {
        const k = (c.name || "").toLowerCase().trim();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    setResults({ cars: uniq, days, from, to, st, et });
    setSelectedId(null);
  }

  // GUID места подачи из URL (форма пишет его при поиске) — нужен брони.
  const deliveryGuid = () => {
    if (typeof window === "undefined") return "";
    return new URL(window.location.href).searchParams.get("delivery") || "";
  };

  // Карточки результата по 2 в ряд; блок брони раскрывается ПОД рядом выбранной.
  const COLS = 2;
  const cars = results?.cars || [];
  const rows: ApiCar[][] = [];
  for (let i = 0; i < cars.length; i += COLS) rows.push(cars.slice(i, i + COLS));
  const selRow = selectedId ? rows.findIndex((r) => r.some((c) => c.guid === selectedId)) : -1;

  return (
    <main style={{ display: "flex", minHeight: "100vh", flexDirection: "column", background: "#fff" }}>
      <div style={{ background: "#F6FAFF" }}>
        <Header theme="light" />
      </div>

      <div style={{ maxWidth: 1200, width: "100%", margin: "0 auto", padding: "28px 20px 60px", flexGrow: 1 }}>
        <style>{`
          .theme-detail-grid { display: grid; grid-template-columns: 1fr; gap: 28px; }
          @media (min-width: 960px) {
            .theme-detail-grid { grid-template-columns: minmax(0, 3fr) minmax(400px, 2fr); gap: 32px; align-items: start; }
            .theme-detail-aside { position: sticky; top: 80px; }
          }
        `}</style>

        {/* Назад + заголовок */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button
            type="button"
            onClick={() => router.back()}
            aria-label={t("a11y.prev")}
            style={{ display: "flex", width: 40, height: 40, flexShrink: 0, cursor: "pointer", alignItems: "center", justifyContent: "center", borderRadius: 12, border: "1px solid #E0E4EC", background: "#fff", color: "#0039A6" }}
          >
            <svg viewBox="0 0 24 24" fill="none" style={{ width: 20, height: 20 }}>
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 style={{ color: "#000", fontSize: "clamp(22px, 5vw, 30px)", fontWeight: "bold", margin: 0, lineHeight: 1.15 }}>{theme.title}</h1>
        </div>

        <div className="theme-detail-grid">
          {/* LEFT — картинка + подзаголовок + полный текст */}
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <div style={{ border: "1px solid #EDEDED", borderRadius: 20, overflow: "hidden", background: "#FFFFFF" }}>
              <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", overflow: "hidden", background: "#F5F5F7" }}>
                <Img src={theme.image || PLACEHOLDER} alt={theme.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </div>
            </div>

            {theme.subtitle ? (
              <p style={{ fontSize: 19, lineHeight: 1.5, color: "#1E1E2D", fontWeight: 600, margin: 0 }}>{theme.subtitle}</p>
            ) : null}

            {theme.content.trim()
              ? <RichContent html={theme.content} />
              : <p style={{ fontSize: 16, lineHeight: 1.7, color: "#8A8A90", margin: 0 }}>{t("theme.descLater")}</p>}
          </div>

          {/* RIGHT — поиск по городу темы + результат прямо тут */}
          <aside className="theme-detail-aside">
            <div style={{ border: "1px solid #EDEDF0", borderRadius: 20, padding: 28, background: "#FFFFFF", boxShadow: "0 8px 32px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#000", marginBottom: 6 }}>
                {city ? t("theme.rentIn", { city: city.name }) : t("theme.rentCar")}
              </div>
              {theme.priceFrom ? (
                <div style={{ marginBottom: 14, color: "#6E6E73", fontSize: 14 }}>
                  {t("price.from")} <span style={{ color: "#d52b1e", fontSize: 22, fontWeight: 800 }}>{theme.priceFrom}</span> {t("price.perDaySlash")}
                </div>
              ) : null}

              {city ? (
                <>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#000", margin: "8px 0 14px" }}>{t("theme.selectDates")}</div>
                  <VerticalSearchForm
                    station={city.station}
                    currency={cur}
                    onSearch={handleSearch}
                    submitLabel={t("theme.findCars")}
                  />

                  {/* РЕЗУЛЬТАТ — прямо в блоке, как «Свободные автомобили» на стр. машины */}
                  {results && (
                    <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #EDEDED" }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#000", margin: "0 0 12px" }}>
                        {cars.length > 0 ? t("theme.available") : t("theme.noneAvailable")}
                      </h3>
                      {cars.length === 0 ? (
                        <p style={{ fontSize: 14, color: "#4A4A50", margin: 0 }}>
                          {t("theme.tryChange")}{" "}
                          <a href={href(city ? `/${city.slug}` : "/rent")} style={{ color: "#0039A6", fontWeight: 700, textDecoration: "none" }}>{t("theme.viewFleet")}</a>
                        </p>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                          {rows.map((row, ri) => (
                            <div key={ri}>
                              <div style={{ display: "flex", gap: 12 }}>
                                {row.map((c) => {
                                  const sel = selectedId === c.guid;
                                  const cp = priceOf(c);
                                  const photo = (c.photos && c.photos[0]) || PLACEHOLDER;
                                  return (
                                    <button
                                      key={c.guid}
                                      type="button"
                                      onClick={() => setSelectedId(sel ? null : c.guid)}
                                      style={{ position: "relative", display: "flex", flexDirection: "column", flex: 1, minWidth: 0, textAlign: "left", cursor: "pointer", padding: 0, background: "#fff", border: "2px solid " + (sel ? "#0039A6" : "#EDEDED"), borderRadius: 14, fontFamily: "inherit" }}
                                    >
                                      <div style={{ position: "relative", width: "100%", aspectRatio: "3 / 2", background: "#F5F5F7", overflow: "hidden", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                                        <Img src={photo} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; (e.target as HTMLImageElement).style.objectFit = "contain"; }} />
                                        <div style={{ position: "absolute", left: 8, top: 8, zIndex: 10 }}>
                                          <CityOfficeBadge city={city?.name} filial={filialOf(c.officeName)} size="sm" />
                                        </div>
                                        <CarPhotoBadge cfg={city?.badgeCar} locale={locale} />
                                      </div>
                                      <div style={{ padding: "10px 12px 12px" }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#1E1E2D", lineHeight: 1.25, marginBottom: 4 }}>{c.name}</div>
                                        {cp > 0 && (
                                          <div>
                                            <span style={{ color: "#d52b1e", fontSize: 15, fontWeight: 800 }}>{cp.toLocaleString(numLocale)} {cur}</span>
                                            <span style={{ color: "#6E6E73", fontSize: 12, marginLeft: 3 }}>{t("price.perDaySlash")}</span>
                                          </div>
                                        )}
                                      </div>
                                      {sel && (
                                        <div style={{ position: "absolute", bottom: -10, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "10px solid transparent", borderRight: "10px solid transparent", borderTop: "10px solid #0039A6" }} />
                                      )}
                                    </button>
                                  );
                                })}
                                {row.length < COLS && Array.from({ length: COLS - row.length }).map((_, k) => <div key={`f${k}`} style={{ flex: 1 }} />)}
                              </div>

                              {selRow === ri && selectedId && results && (() => {
                                const car = cars.find((x) => x.guid === selectedId)!;
                                return (
                                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #EDEDED" }}>
                                    <BookingPanel
                                      car={{ id: car.guid, title: car.name, price: priceOf(car) }}
                                      city={{ station: city.station, slug: city.slug }}
                                      rentalDays={results.days}
                                      dateFrom={results.from}
                                      dateTo={results.to}
                                      startTime={results.st}
                                      endTime={results.et}
                                      regionGuid={deliveryGuid()}
                                      onClose={() => setSelectedId(null)}
                                      currency={cur}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p style={{ fontSize: 14, color: "#4A4A50", margin: "10px 0 0" }}>
                  {t("theme.noCity")}{" "}
                  <a href={href("/rent")} style={{ color: "#0039A6", fontWeight: 700, textDecoration: "none" }}>{t("theme.wholeFleet")}</a>
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </main>
  );
}
